import { forwardRef, useEffect, useRef, type FocusEvent, type MouseEvent } from 'react';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { Box, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';
import type { CountyPreviewSource } from '@/hooks/useGuestAddress';
import type { ExactLocationState } from '@/hooks/useExactLocation';

interface CountyPreviewNoticeProps {
  place: string;
  source: CountyPreviewSource;
  /** Shared with the tip above the ballot. */
  exact: ExactLocationState;
}

/**
 * Closes a county-preview ballot with the top tip's wording, so a voter who
 * scrolled past (or closed) the tip still learns the ballot is estimated. The
 * "Estimated address" chip scrolls here, so it takes focus.
 */
const CountyPreviewNotice = forwardRef<HTMLElement, CountyPreviewNoticeProps>(
  function CountyPreviewNotice({ place, source, exact }, ref) {
    const { t } = useTranslation('ballot');
    const isGuess = source === 'ip';
    // When the link unmounts (a denial, or an approximate fix), a keyboard
    // user's focus moves to the message that replaces it.
    const linkFocusedRef = useRef(false);
    const messageRef = useRef<HTMLSpanElement>(null);
    useEffect(() => {
      if (!exact.offered && linkFocusedRef.current) {
        linkFocusedRef.current = false;
        messageRef.current?.focus();
      }
    }, [exact.offered]);
    // SampleBallotView announces these; this copy is for sighted voters and focus.
    const message =
      exact.locating || !exact.outcome
        ? ''
        : exact.outcome === 'approximate'
          ? t('countyPreview.approximateLocation')
          : t('countyPreview.noticeLocationFailed');
    return (
      <Box
        ref={ref}
        component="section"
        tabIndex={-1}
        aria-labelledby="county-preview-notice-title"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          mx: 1,
          mt: 1,
          px: 1.5,
          py: 1.25,
          backgroundColor: semanticColors.warningBackground,
          border: `1px solid ${semanticColors.warningBannerBorder}`,
          borderRadius: BORDER_RADIUS,
          color: semanticColors.warningText,
          '&:focus-visible': {
            outline: `2px solid ${semanticColors.warningText}`,
            outlineOffset: '2px',
          },
        }}
      >
        <LocationOnOutlinedIcon fontSize="small" aria-hidden="true" sx={{ mt: '1px' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            id="county-preview-notice-title"
            component="h2"
            variant="body2"
            sx={{ fontWeight: 600, color: 'inherit' }}
          >
            {t(isGuess ? 'countyPreview.guessTitle' : 'countyPreview.title', { place })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'inherit', lineHeight: 1.45 }}>
            {t(isGuess ? 'countyPreview.noticeGuessBody' : 'countyPreview.noticeBody')}
          </Typography>
          {message && (
            <Typography
              ref={messageRef}
              variant="caption"
              tabIndex={-1}
              sx={{
                color: 'inherit',
                lineHeight: 1.45,
                mt: 0.5,
                '&:focus-visible': {
                  outline: `2px solid ${semanticColors.warningText}`,
                  outlineOffset: '2px',
                },
              }}
            >
              {message}
            </Typography>
          )}
          {/* Stays through locating and a retryable failure. A denial removes it: a
              browser that refused keeps refusing without asking again. */}
          {exact.offered && (
            <Typography variant="caption" sx={{ color: 'inherit', lineHeight: 1.45 }}>
              <Link
                component="button"
                type="button"
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                  linkFocusedRef.current = event.currentTarget.matches(':focus-visible');
                  exact.locate('ballot_notice', linkFocusedRef.current);
                }}
                onBlur={(event: FocusEvent<HTMLButtonElement>) => {
                  // Only a move elsewhere; unmounting leaves no relatedTarget.
                  if (event.relatedTarget) linkFocusedRef.current = false;
                }}
                aria-disabled={exact.locating || undefined}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: 24,
                  color: 'inherit',
                  fontWeight: 600,
                  textAlign: 'start',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  '&[aria-disabled="true"]': { cursor: 'progress' },
                  '&:focus-visible': {
                    outline: `2px solid ${semanticColors.warningText}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                {t('countyPreview.useExactLocation')}
              </Link>
            </Typography>
          )}
          {/* DB-IP's free database is CC BY 4.0, which asks for this credit
              wherever its data is shown. Their wording, so it stays English. */}
          {isGuess && (
            <Typography variant="caption" sx={{ color: 'inherit', lineHeight: 1.45 }}>
              <Link
                href="https://db-ip.com"
                target="_blank"
                rel="noopener noreferrer"
                lang="en"
                sx={{ color: 'inherit' }}
              >
                IP Geolocation by DB-IP
              </Link>
            </Typography>
          )}
        </Box>
      </Box>
    );
  }
);

export default CountyPreviewNotice;
